'use client';
import { useEffect, useState } from 'react';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import Image from 'next/image';
import { usePathname } from "next/navigation";
import MinimalPushOverlayMenu from './NewMenu';
import Letstalk from './LetsTalk';
import Nav from '@/components/Nav/Nav'
import { ViewTransitions } from "next-view-transitions";

const industries = [
  {
    link:"/business-professional",
    category: "Business & Professional",
    items: ["Professional Services", "Consulting", "Legal", "Finance & Fintech", "Marketing & Advertising", "Recruitment & HR"],
  },
  {
    link:"/Commerce-Hospitality",
    category: "Commerce & Hospitality",
    items: ["E-commerce & Retail", "Restaurants", "Hotels", "Travel & Tourism", "Fashion", "Beauty"],
  },
  {
    link:"/Technology-Digital",
    category: "Technology & Digital",
    items: ["Technology", "SaaS", "Startups", "Media", "Telecommunications"],
  },
  {
    link:"/Property-Construction",
    category: "Property & Construction",
    items: ["Real Estate", "Property Management", "Construction", "Architecture & Interior Design"],
  },
  {
    link:"/Health-Lifestyle",
    category: "Health & Lifestyle",
    items: ["Healthcare", "Clinics", "Wellness", "Fitness & Sports"],
  },
  {
    link:"/Education-Public",
    category: "Education & Public",
    items: ["Education", "E-Learning", "Nonprofits", "Government & Public Services"],
  },
] as const;

const services = [
  { name: "UI/UX design", link: "/ui-ux" },
  { name: "Web development and SEO", link: "/web-seo" },
  { name: "Testing and Consulting", link: "/testing-consulting" },
  { name: "Maintenance & Support", link: "/maintenance-support" },
]as const;

export default function NavBar() {
  const l = useLocale();
  const th = useTranslations('Padel-Master-The-Game-Home-header');
  const pathname = usePathname();

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Shared inner content so both nav states render identically
  const subNavContent = (
    <>
      <div className="group relative">
        <button className="flex items-center gap-1.5 hover:text-primary">
          Services
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:rotate-180"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <div className="absolute left-1/2 top-full w-72 -translate-x-1/2 translate-y-0 pt-5 opacity-0 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:pointer-events-auto">
          <div className="bg-secondary/90 text-neutral-800 rounded-3xl shadow-2xl p-3 font-normal">
            {services.map((item, i) => (
              <Link
                key={i}
                href={item.link}
                className="block px-5 py-3 rounded-2xl text-base hover:bg-secondary/10 hover:text-primary transition-colors duration-200"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Link href={"/studies"}>
        <button className='hover:text-primary'>Case Studies</button>
      </Link>

      <div className="group relative">
        <button className="flex items-center gap-1.5 hover:text-primary">
          Industry
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:rotate-180"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <div
          className="absolute left-1/2 top-full -translate-x-1/2 translate-y-0 pt-5 opacity-0 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:pointer-events-auto"
          style={{ width: "min(90vw, 900px)" }}
        >
          <div className="bg-secondary/90 text-neutral-800 rounded-3xl shadow-2xl p-10 font-normal grid grid-cols-3 gap-x-10 gap-y-8">
            {industries.map((group) => (
              <div key={group.category} className="group/item">
                <Link href={group.link}>
                  <h5 className="text-primary font-bold text-base my-3 group-hover/item:underline group-hover/item:decoration-white">
                    {group.category}
                  </h5>
                </Link>

                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <li key={item}>
                      <p className="text-[15px] text-neutral-600 group-hover/item:text-primary transition-colors duration-200">
                        {item}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div> 
     
        </div>
         
      </div>

      <Link href={"/trends"}><button className='hover:text-primary'>Digital Trends</button></Link>
      <Link href={"/about-us"}><button className='hover:text-primary'>About</button></Link>
      <Link href={"/contact-us"}><button className='hover:text-primary'>Contact</button></Link>
          
      <Letstalk/>
    </>
  );

  // Hidden on mobile, flex layout from lg breakpoint up
  const subNavClasses =
    "hidden lg:flex justify-between items-center py-2 font-bold mt-4 px-6 gap-10 xl:text-lg rounded-full";

  return (
    <header className="absolute top-0 left-0 w-full z-[9999] text-white ">

      <nav
        className="flex justify-between items-center py-2 mt-4 lg:px-5 gap-4 mx-2 lg:mx-16"
        aria-label="Global"
      >
        
        <Link href="/" rel="preload" className="relative z-[3]">
          <div className="relative -mr-2 lg:-mr-0 h-6 w-40 md:h-10 md:w-56">
            <Image
              src="/evinov.avif"
              alt="evinov"
              fill
              sizes="(max-width: 640px) 160px, (max-width: 768px) 224px, 288px"
              style={{ objectFit: 'contain' }}
              priority
            />
          </div>
        </Link>

        {/* Static in-flow sub-nav — desktop only, fades/slides OUT smoothly on scroll */}
        <div
          className={`${subNavClasses} transition-all duration-500 ease-out  ${
            pathname === "/" ? "bg-secondary/80" : ""
          } ${
            scrolled
              ? "opacity-0 -translate-y-2 pointer-events-none"
              : "opacity-100 translate-y-0"
          }`}
          aria-hidden={scrolled}
        >
          {subNavContent}
        </div>

        {/* Mobile hamburger nav — mobile only, hidden from lg breakpoint up */}
        <div className="lg:hidden">
          <ViewTransitions>
            <Nav />
          </ViewTransitions>
        </div>
      </nav>

      {/* Fixed floating sub-nav — desktop only, fades/slides IN smoothly on scroll */}
      <div
        className={`hidden lg:flex fixed left-1/2 -translate-x-1/2 z-50 w-fit bg-secondary/80 shadow-lg backdrop-blur-sm text-white transition-all duration-500 ease-out justify-between items-center py-2 font-bold mt-4 px-6 gap-10  rounded-full ${
          scrolled
            ? "top-4 opacity-100 translate-y-0 pointer-events-auto"
            : "top-0 opacity-0 -translate-y-3 pointer-events-none"
        }`}
        aria-hidden={!scrolled}
      >
        {subNavContent}
      </div>
    </header>
  );
}